import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Zap, ShieldCheck } from "lucide-react";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PayWall() {
  const { user, refresh } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get("/payment/config"), api.get("/payment/status")]);
      setCfg(c.data); setStatus(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (status?.access_status === "active") refresh();
  }, [status, refresh]);

  const payWithRazorpay = async () => {
    setSubmitting(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Failed to load Razorpay. Check your internet connection.");
      const { data } = await api.post("/payment/razorpay/create-order");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "RIF Master",
        description: "Lifetime access",
        prefill: { name: user.name, email: user.email, contact: user.phone || "" },
        notes: { user_email: user.email },
        theme: { color: "#1e3a8a" },
        config: {
          display: {
            blocks: {
              upi: { name: "Pay using UPI", instruments: [{ method: "upi" }] },
              other: {
                name: "Other payment methods",
                instruments: [
                  { method: "card" },
                  { method: "netbanking" },
                  { method: "wallet" },
                ],
              },
            },
            sequence: ["block.upi", "block.other"],
            preferences: { show_default_blocks: false },
          },
        },
        handler: async (resp) => {
          try {
            await api.post("/payment/razorpay/verify", resp);
            toast.success("Payment verified. Your access is now active.");
            await load();
            await refresh();
          } catch (e) {
            toast.error(formatApiError(e));
          }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      });
      rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed. Please try again."));
      rzp.open();
    } catch (err) {
      toast.error(err.message || formatApiError(err));
    } finally { setSubmitting(false); }
  };

  if (!cfg) return <div className="text-slate-500">Loading payment details…</div>;

  const isActive = user?.access_status === "active";

  return (
    <div className="reveal max-w-3xl">
      <div className="eyebrow">Step 2 of 2</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Unlock lifetime access</h1>
      <p className="mt-2 text-slate-600 max-w-2xl">
        One-time payment of <span className="font-mono font-semibold text-slate-900">₹{cfg.amount.toLocaleString("en-IN")}</span>. Access unlocks automatically the moment your payment succeeds — no waiting for manual verification.
      </p>

      {isActive && (
        <div className="mt-6 border-l-4 border-teal-700 bg-teal-50 p-4 rounded-sm flex items-start gap-3" data-testid="paywall-active-banner">
          <CheckCircle2 className="h-5 w-5 text-teal-700 shrink-0 mt-0.5" strokeWidth={1.75} />
          <div>
            <div className="font-semibold text-slate-900">Your account is active.</div>
            <div className="text-sm text-slate-600 mt-1">You have full lifetime access. Head to the dashboard to start a case.</div>
          </div>
        </div>
      )}

      {cfg.razorpay_enabled && !isActive && (
        <div className="mt-8 border-2 border-blue-900 bg-white rounded-md p-8">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-blue-900" strokeWidth={1.75} />
            <div className="eyebrow text-blue-900">Instant activation</div>
          </div>
          <div className="mt-3 font-heading text-3xl font-extrabold text-slate-900">Pay ₹{cfg.amount.toLocaleString("en-IN")}</div>
          <div className="text-sm text-slate-600 mt-2">
            Powered by Razorpay — pay via UPI (GPay, PhonePe, Paytm, BHIM), credit / debit card, netbanking, or wallets. Verified automatically, access unlocks in seconds.
          </div>

          <Button
            onClick={payWithRazorpay}
            disabled={submitting}
            className="mt-6 h-12 px-8 bg-blue-900 hover:bg-blue-800 text-base"
            data-testid="razorpay-pay-btn">
            {submitting ? "Opening secure checkout…" : `Pay ₹${cfg.amount.toLocaleString("en-IN")} securely`}
          </Button>

          <div className="mt-6 flex items-start gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" strokeWidth={1.75} />
            <div>
              256-bit encrypted checkout hosted by Razorpay. We never see your card or UPI credentials. A receipt is emailed to
              <span className="font-mono ml-1 text-slate-700">{user.email}</span> on success.
            </div>
          </div>
        </div>
      )}

      {!cfg.razorpay_enabled && !isActive && (
        <div className="mt-8 border-l-4 border-amber-500 bg-amber-50 p-4 rounded-sm text-sm text-slate-700" data-testid="paywall-payment-unavailable">
          Online payment is temporarily unavailable. Please contact
          <a href="mailto:drmonashroff@gmail.com" className="underline ml-1">drmonashroff@gmail.com</a>
          {" "}to arrange access.
        </div>
      )}

      <div className="mt-8 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-200 pt-4">
        <span>By paying you agree to our</span>
        <Link to="/terms" className="hover:text-slate-900 underline">Terms &amp; Conditions</Link>
        <Link to="/refund" className="hover:text-slate-900 underline">Refund &amp; Cancellation</Link>
        <Link to="/privacy" className="hover:text-slate-900 underline">Privacy Policy</Link>
        <Link to="/contact" className="hover:text-slate-900 underline">Contact Us</Link>
      </div>
    </div>
  );
}
